import { Component, ErrorInfo, ReactNode } from "react";
import { trackJsError } from "@/lib/tracking";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  componentName?: string;
}

interface State {
  hasError: boolean;
  isChunkError: boolean;
}

/**
 * Captura errores de rendering en el árbol de React.
 * - Reporta a GA4 + Supabase via trackJsError con mensaje + componentStack.
 * - Detecta errores de chunk loading (deploys mid-session, red caída) y
 *   ofrece recargar en lugar de mostrar pantalla en blanco.
 */
const RELOAD_GUARD_KEY = "_qdm_chunk_reload_count";
const MAX_AUTO_RELOADS = 1;

class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, isChunkError: false };

  static getDerivedStateFromError(error: Error): State {
    const msg = String(error?.message || error || "");
    const isChunkError =
      /Loading chunk|ChunkLoadError|Failed to fetch dynamically imported module|Importing a module script failed/i.test(
        msg
      );
    return { hasError: true, isChunkError };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    try {
      const componentName = this.props.componentName || "root";
      const stackLine =
        (errorInfo?.componentStack || "")
          .split("\n")
          .find((l) => l.trim().startsWith("at "))
          ?.trim() || componentName;
      trackJsError(
        `[react] ${error?.name || "Error"}: ${error?.message || "unknown"}`,
        stackLine.slice(0, 200)
      );
    } catch {
      /* never throw inside boundary */
    }

    // Auto-recargar UNA sola vez si es ChunkLoadError (deploy mid-sesión).
    // Guard con sessionStorage para evitar loop infinito de recargas.
    if (this.state.isChunkError) {
      try {
        const count = parseInt(sessionStorage.getItem(RELOAD_GUARD_KEY) || "0", 10) || 0;
        if (count < MAX_AUTO_RELOADS) {
          sessionStorage.setItem(RELOAD_GUARD_KEY, String(count + 1));
          setTimeout(() => window.location.reload(), 400);
        }
      } catch {
        /* sessionStorage bloqueado: no auto-reload */
      }
    }
  }

  handleReload = () => {
    try {
      // Reset del guard cuando el usuario fuerza recarga manual
      sessionStorage.removeItem(RELOAD_GUARD_KEY);
      window.location.reload();
    } catch {
      try { window.location.reload(); } catch { /* ignore */ }
    }
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    if (this.props.fallback) return this.props.fallback;

    return (
      <div
        role="alert"
        className="min-h-[40vh] flex flex-col items-center justify-center text-center p-6 bg-background"
      >
        <h2 className="font-display font-extrabold text-xl text-foreground mb-2">
          {this.state.isChunkError
            ? "Hubo un problema al cargar la página"
            : "Algo salió mal"}
        </h2>
        <p className="text-sm text-muted-foreground mb-4 max-w-md">
          {this.state.isChunkError
            ? "Recarga la página para obtener la versión más reciente."
            : "Por favor recarga la página. Si el problema persiste, intenta de nuevo en unos minutos."}
        </p>
        <button
          onClick={this.handleReload}
          className="bg-primary hover:bg-[#C71D18] text-white font-bold px-6 py-3 rounded-full"
        >
          Recargar
        </button>
      </div>
    );
  }
}

export default ErrorBoundary;
